import { Component, OnInit } from '@angular/core';
import { ModalController, NavParams, LoadingController, AlertController } from '@ionic/angular';
import { OrderService } from './../../services/order.service';
import * as moment from 'moment-timezone';
import { ConnectedAccountService } from '../../services/connected-account.service';
import { StorageService } from '../../services/storage.service';
import { DateTimePickerPage } from './../../modals/date-time-picker/date-time-picker.page';
import { ObserverService } from './../../services/observer.service';
import { UserService } from './../../services/user.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-order-return-edit',
    templateUrl: './order-return-edit.page.html',
    styleUrls: ['./order-return-edit.page.scss'],
})

export class OrderReturnEditPage implements OnInit {

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    moment = moment;
    user = null;
    params = {};
    order_id = null;
    return_id = null;
    return = null;
    formatted_date = null;
    date_modal_css_class = '';

    // modal flags
    date_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public navParams: NavParams,
        public alertController: AlertController,
        private loadingController: LoadingController,
        private orderService: OrderService,
        private connectedAccountService: ConnectedAccountService,
        private storageService: StorageService,
        private observerService: ObserverService,
        private userService: UserService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.date_modal_css_class = 'backdrop';
        }

        if(this.navParams.get('return_id')) {

            this.return_id = this.navParams.get('return_id');

            if(this.navParams.get('order_id')) {

                this.order_id = this.navParams.get('order_id');

                this.loadData().then(() => {

                    if(this.return.date) {
                        this.params['date'] = this.moment.utc(this.return.date).tz(this.user.timezone.code).toISOString(true);
                        this.formatted_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");
                    }
                    else {
                        this.params['date'] = "";
                    }

                    if(this.return.reason) {
                        this.params['reason'] = this.return.reason;
                    }
                    else {
                        this.params['reason'] = "";
                    }

                })
                .catch(() => { })

            }

        }

        this.observerService.subscribe('network_status_changed|order_return_edit_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Problem", "Offline", "You must be online to edit a return, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|order_return_edit_modal");
    }

    loadData() {
        let promise = new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading order return").then(() => {
                this.loadUser().then(() => {
                    this.loadOrderReturn().then(() => {
                        this.loading_spinner.dismiss();
                        resolve();
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
        return promise;
    }

    loadUser() {
        let promise = new Promise<void>((resolve, reject) => {
            this.user = null;
            this.storageService.get("connected_account_id").then((connected_account_id) => {
                if(connected_account_id) {
                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account: any) => {
                        if(connected_account['user_id']) {
                            this.userService.getUser(connected_account['user_id']).then((user: any) => {
                                this.user = user;
                            });
                        }
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
                }
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    loadOrderReturn() {
        let promise = new Promise<void>((resolve, reject) => {
            this.return = null;
            this.orderService.getOrderReturn(this.order_id, this.return_id).then((order_return) => {
                if(order_return) {
                    this.return = order_return;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    close() {
        this.modalController.dismiss();
    }

    update() {

        let validated = true;

        if(this.params['date'] == null || this.params['date'].trim() === "") {
            validated = false;
            this.presentAlert('Problem', 'Missing date', 'Please select a date.');
        }

        if(validated) {

            this.params['date'] = this.moment(this.params['date']).utc().toISOString();

            this.updateOrderReturn().then(() => {
                this.modalController.dismiss();
            });

        }

    }

    updateOrderReturn() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Updating return").then(() => {
                this.orderService.updateOrderReturn(this.order_id, this.return_id, this.params).then(() => {
                    this.spinner.dismiss();
                    resolve();
                })
                .catch(() => {
                    this.spinner.dismiss();
                    reject();
                });
            });
        });
    }

    async presentDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: (this.params['date'] ? this.params['date'] : ""),
                    format: "date-time",
                    clear_date: false
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    this.params['date'] = data.data['date'];
                    this.formatted_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
